// react
import { Suspense } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useParams } from '@tanstack/react-router';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

// material ui
import {
  alpha,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

// api
import { useGetBasicEnergyAccountQuery } from '@/api/energy-analysis/basicEnergySlice';

// feature-components
import ServiceActivationSwitch from '@/features/service-manager/components/ServiceActivationSwitch';

// components
import { formatTimestamp } from '@/utils/formatters';

export const Route = createFileRoute(
  '/$accountId/_admin/service-manager/$managedAccountId/dashboard-power-basic',
)({
  component: RouteComponent,
  staticData: { crumb: 'service-manager:services.basicDashboards.title' },
});

function RouteComponent() {
  const { t, i18n } = useTranslation('service-management');
  const { managedAccountId } = useParams({
    from: '/$accountId/_admin/service-manager/$managedAccountId/dashboard-power-basic',
  });

  const { data: basicEnergySubscription } = useGetBasicEnergyAccountQuery({
    account_id: managedAccountId,
  });

  const included = !!basicEnergySubscription?.Power;
  const synced = !!basicEnergySubscription?.PowerInSync;

  console.log('basicEnergySubscription', basicEnergySubscription);

  const renderChip = () => {
    if (!included) {
      return (
        <Chip
          color="error"
          variant="outlined"
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.14),
          }}
          label={'Not included'}
        />
      );
    }
    if (included && !synced) {
      return (
        <Chip
          color="warning"
          variant="outlined"
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.14),
          }}
          label={'Currently not in sync'}
        />
      );
    }
    return (
      <Chip
        color="success"
        icon={<CheckCircleRoundedIcon />}
        variant="outlined"
        sx={{
          backgroundColor: (theme) => alpha(theme.palette.success.main, 0.14),
        }}
        label={
          'Latest cache update: ' +
          formatTimestamp(
            basicEnergySubscription?.LastPowerCacheTimestamp,
            i18n.resolvedLanguage,
          )
        }
      />
    );
  };

  return (
    <Stack spacing={2}>
      <Suspense>
        <Card>
          <CardHeader
            title={t('basicPower.title')}
            action={
              <ServiceActivationSwitch
                managedAccountId={managedAccountId}
                servicePath="dashboard-power-basic"
                ariaLabel={t('basicPower.title')}
                basicEnergyType="Power"
              />
            }
            slotProps={{
              title: { variant: 'h6', fontWeight: 600 }
            }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('basicEnergy.description')}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {renderChip()}
              </Stack>
            </Stack>
          </CardContent>
          <Divider />
        </Card>
      </Suspense>
    </Stack>
  );
}
